import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { AppAccount } from 'types/apps';
import { xhrStatusUIState } from '../reusable/xhrStatus';
import { getHasNoProjectLimit, getNumberOfAvailableProjects } from '../selectors/accounts';

export const getAppsEntities = (state: IState) => state.apps.appsEntities;
const readWorkspaceEntities = (state: IState) => state.integrations.entities.integrations;
const readWorkspaceResults = (state: IState) => state.integrations.result;
const readWorkspaceXhr = (state: IState) => state.integrations.xhrStatus;
const readSelectedWorkspace = (state: IState) => state.integrations.selectedWorkspace;
const readWorkspaceProjects = (state: IState) => state.integrations.projects;
const readImportState = (state: IState) => state.integrations.import;
const readSteps = (state: IState) => state.integrations.steps;
const readStepEntities = (state: IState) => state.integrations.stepEntities;
const readProjectsQuery = (state: IState) => state.integrations.projects.query;
const readIsImporting = (state: IState) => state.integrations.import.inProgress;
const readCurrentImportStatus = (state: IState) => state.integrations.import.status;
const readImportAccountId = (state: IState) => state.integrations.importAccountId;
const readImportSuccessFrom = (state: IState) => state.integrations.import.importSuccessFrom;
const readImportingFrom = (state: IState) => state.integrations.import.importingFrom;
const readAppAccounts = (state: IState) => state.apps.accounts;
const readImportSelections = (state: IState) => state.integrations.selection;

export const getImportAccountId = readImportAccountId;
export const getImportingFrom = readImportingFrom;
export const getImportSuccessFrom = readImportSuccessFrom;

export const getAccountToAppIdMap = createSelector(
  [readAppAccounts],
  (appAccounts): Record<number, number> => {
    return Object.keys(appAccounts).reduce(
      (record, appId) => {
        const accounts: Array<AppAccount> = appAccounts[+appId];

        const accountsToId = accounts.reduce(
          (map, appAccount) => {
            return {
              ...map,
              [appAccount.id]: +appId,
            };
          },
          {} as Record<number, number>
        );

        return {
          ...record,
          ...accountsToId,
        };
      },
      {} as Record<number, number>
    );
  }
);

export const getCurrentImportStatus = readCurrentImportStatus;
export const getImportState = readImportState;
export const getIsImporting = readIsImporting;

export const getProjectsQuery = readProjectsQuery;

export const getProjectsArray = createSelector([readWorkspaceProjects], projects => {
  return projects.result.map(projectId => projects.entities[projectId]);
});

export const getWorkspaceProjectIds = createSelector([readWorkspaceProjects], workspaceProjects => {
  return workspaceProjects.result;
});

export const getProjectsBasedOnQuery = createSelector(
  [getProjectsQuery, getProjectsArray],
  (query, projects) => {
    return query
      ? projects.filter(project => project.name.toLowerCase().includes(query.toLowerCase()))
      : projects;
  }
);

export const getWorkspaceProjects = createSelector([getProjectsBasedOnQuery], projects => {
  return projects.reduce((prev: any, curr: any) => {
    return [
      ...prev,
      {
        status: 1,
        item: {
          value: curr.id,
          ...curr,
        },
      },
    ];
  }, []);
});

export const getSteps = readSteps;

export const getStepEntities = readStepEntities;

export const getIsReadyToImport = createSelector(
  [getSteps, getStepEntities],
  (steps, stepEntities) => {
    return steps.every(step => {
      const currentStep = stepEntities[step];
      return currentStep.enabled;
    });
  }
);

export const getSelectedProjectIdsForImport = createSelector([readImportSelections], selections => {
  return new Set(selections.projectIds);
});

export const getSelectedWorkspace = readSelectedWorkspace;

export const getWorkspaceEntries = createSelector(
  [readWorkspaceEntities, readWorkspaceResults],
  (entities, results) => {
    return {
      entities,
      results,
    };
  }
);

export const getCurrentImportAccountId = createSelector([readImportState], importState => {
  return importState.accountId;
});

export const getEntriesForImportStep = createSelector([getWorkspaceEntries], entries => {
  const { results, entities } = entries;
  return results.reduce((prev: any, curr: any) => {
    const currentEntry = entities[curr];

    return [
      ...prev,
      {
        value: currentEntry.id,
        ...currentEntry,
      },
    ];
  }, []);
});

export const getIntegrationsXhr = createSelector([readWorkspaceXhr], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

export const getIntegrationsIsLoading = createSelector([getIntegrationsXhr], xhrStatus => {
  return xhrStatus.isLoading;
});

export const getHasSelectedAll = createSelector(
  [getSelectedProjectIdsForImport, getNumberOfAvailableProjects, getProjectsArray],
  (selectedIds, availableProjects, workspaceProjects) => {
    if (!selectedIds.size) {
      return false;
    }
    return (
      (availableProjects > 0 && availableProjects === selectedIds.size) ||
      selectedIds.size === workspaceProjects.length
    );
  }
);

export const getCurrentAppName = createSelector(
  [getAppsEntities, getImportAccountId, getAccountToAppIdMap],
  (apps, appId, accountToIdMap) => {
    if (!appId) {
      return '';
    }
    const id = accountToIdMap[appId];
    const currentApp = apps[id];
    return currentApp.display_name;
  }
);

export const getRemainingProjectsAvailable = createSelector(
  [getSelectedProjectIdsForImport, getNumberOfAvailableProjects],
  (selectedProjects, availableProjects) => {
    return availableProjects - selectedProjects.size;
  }
);

export const getIsSelectionDisabled = createSelector(
  [getRemainingProjectsAvailable, getHasNoProjectLimit],
  (remainingProjects, hasNoProjectLimit) => {
    return !remainingProjects && !hasNoProjectLimit;
  }
);

export const getQueryStatus = createSelector(
  [getIntegrationsIsLoading, getProjectsQuery, getWorkspaceProjects],
  (isLoading, query, projects) => {
    const loadedWithNoProjectsNoQuery = !isLoading && !projects.length && !query;
    const noSearchResults = !isLoading && !projects.length && query.length > 0;
    return {
      loadedWithNoProjectsNoQuery,
      noSearchResults,
    };
  }
);
